import { useSelector, useDispatch } from "react-redux";
import { useMemo } from "react";

import {
  initializeTable,
  updateTableCell,
  updateTableState,
  submitTableAnswers,
  selectTableAttemptsLeft,
  selectTableModifiedCells,
  selectTableValues,
  type TableQuestionState,
} from "./TableQuestionSlice";

export interface TableQuestionReduxState {
  attemptsLeft: number | undefined;
  modifiedCells: string[] | undefined;
  cellValues: Record<string, string | number | null> | undefined;
}

export interface TableQuestionActions {
  initialize: (params: {
    attemptsLeft?: number;
    initialValues?: Record<
      string,
      { value: string | number | null; isSubmitted: boolean }
    >;
  }) => void;
  updateCell: (params: {
    rowId: number;
    colId: number;
    value: string | number | null;
  }) => void;
  updateState: (params: {
    attemptsLeft?: number;
    isSubmitted?: boolean;
    modifiedCells?: string[];
  }) => void;
  submitAnswers: (params: { correct?: boolean }) => void;
}

export const useTableQuestion = (
  tableId: string | number
): [TableQuestionReduxState, TableQuestionActions] => {
  const dispatch = useDispatch();

  const attemptsLeft = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableAttemptsLeft(state, tableId)
  );
  const modifiedCells = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableModifiedCells(state, tableId)
  );
  const cellValues = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableValues(state, tableId)
  );

  const state: TableQuestionReduxState = useMemo(
    () => ({ attemptsLeft, modifiedCells, cellValues }),
    [attemptsLeft, modifiedCells, cellValues]
  );

  const actions = useMemo<TableQuestionActions>(
    () => ({
      initialize: (params) => dispatch(initializeTable({ tableId, ...params })),
      updateCell: (params) => dispatch(updateTableCell({ tableId, ...params })),
      updateState: (params) =>
        dispatch(updateTableState({ tableId, ...params })),
      submitAnswers: (params) =>
        dispatch(submitTableAnswers({ tableId, ...params })),
    }),
    [dispatch, tableId]
  );

  return [state, actions];
};
